import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Table, Button, Alert, 
  Badge, InputGroup, Form, Dropdown
} from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaCog } from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';
import AdminLayout from '../../components/layout/AdminLayout';
import SystemModal from '../../components/modals/SystemModal';
import supabase from '../../utils/supabaseClient';

const SystemManagement = () => {
  const { isAdmin } = useAuth();
  
  // Estados principais
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Estados do modal
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [selectedSystem, setSelectedSystem] = useState(null);

  // Estados para filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (isAdmin) {
      loadSystems();
    }
  }, [isAdmin]);

  const loadSystems = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('systems')
        .select(`
          *,
          system_roles!system_id (count)
        `)
        .order('name');

      if (error) throw error;
      setSystems(data || []);
    } catch (error) {
      console.error('Erro ao carregar sistemas:', error);
      setError('Erro ao carregar sistemas');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (type, system = null) => {
    setModalType(type);
    setSelectedSystem(system);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedSystem(null);
    setModalType('create');
  };

  const handleModalSuccess = (message) => {
    setSuccess(message);
    loadSystems();
    setTimeout(() => setSuccess(null), 5000);
  };

  const handleDeleteSystem = async (systemId, systemName) => {
    if (!window.confirm(`Tem certeza que deseja excluir o sistema "${systemName}"?`)) {
      return;
    }

    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('systems')
        .delete()
        .eq('id', systemId);

      if (error) throw error;

      setSuccess(`Sistema "${systemName}" removido com sucesso!`);
      await loadSystems();
    } catch (error) {
      console.error('Erro ao excluir sistema:', error);
      setError(`Erro ao excluir sistema: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const filteredSystems = systems.filter(system => {
    const matchesSearch = 
      system.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      system.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      system.slug?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && system.active) ||
      (statusFilter === 'inactive' && !system.active);

    return matchesSearch && matchesStatus;
  });

  const formatarData = (dataString) => {
    return new Date(dataString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (!isAdmin) {
    return (
      <div className="text-center mt-5">
        <h3>Acesso Negado</h3>
        <p>Você não tem permissão para acessar esta área.</p>
      </div>
    );
  }

  return (
    <AdminLayout>
      <Container fluid>
        {/* Cabeçalho */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="mb-1">
                  <FaCog className="me-2" />
                  Gerenciamento de Sistemas
                </h1>
                <p className="text-muted mb-0">
                  Configure e gerencie os sistemas disponíveis no hub
                </p>
              </div>
              
              <div className="d-flex gap-2">
                <Button 
                  variant="outline-primary" 
                  onClick={loadSystems}
                  disabled={loading}
                >
                  🔄 Atualizar
                </Button>
                <Button 
                  variant="primary" 
                  onClick={() => openModal('create')}
                >
                  <FaPlus className="me-2" />
                  Novo Sistema
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Mensagens */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Filtros */}
        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={6}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Buscar por nome, descrição ou slug..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={4}>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">Todos os status</option>
                  <option value="active">Apenas ativos</option>
                  <option value="inactive">Apenas inativos</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <Button 
                  variant="outline-secondary" 
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                  }}
                >
                  Limpar
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tabela de Sistemas */}
        <Card>
          <Card.Header>
            <h5 className="mb-0">
              Sistemas Cadastrados ({filteredSystems.length})
            </h5>
          </Card.Header>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Sistema</th>
                  <th>Slug/URL</th>
                  <th>Perfis</th>
                  <th>Status</th>
                  <th>Criado em</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredSystems.map(system => (
                  <tr key={system.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <span className="me-2" style={{ fontSize: '1.5em' }}>
                          {system.icon || '🔧'}
                        </span>
                        <div>
                          <strong>{system.name}</strong>
                          <br />
                          <small className="text-muted">{system.description}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <code>{system.slug}</code>
                        <br />
                        <small className="text-muted">
                          {system.route || 'Não configurada'}
                        </small>
                      </div>
                    </td>
                    <td>
                      <Badge bg="info">
                        {system.system_roles?.[0]?.count || 0} perfis
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={system.active ? 'success' : 'danger'}>
                        {system.active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td>{formatarData(system.created_at)}</td>
                    <td>
                      <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm">
                          Ações
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => openModal('edit', system)}>
                            <FaEdit className="me-2" />
                            Editar
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item 
                            className="text-danger"
                            onClick={() => handleDeleteSystem(system.id, system.name)}
                          >
                            <FaTrash className="me-2" />
                            Excluir
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            
            {filteredSystems.length === 0 && !loading && (
              <div className="text-center py-4">
                <p className="text-muted">Nenhum sistema encontrado.</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Modal de Sistema */}
        <SystemModal
          show={showModal}
          onHide={closeModal}
          modalType={modalType}
          selectedSystem={selectedSystem}
          onSuccess={handleModalSuccess}
        />
      </Container>
    </AdminLayout>
  );
};

export default SystemManagement;